import config from '../../config';
import { GET_LIST, GET_ONE, UPDATE } from '../../api/dataProvider';
import { KNOWLEDGE_REVIEW_STAFF_EMAILS } from '../knowledgeReview/knowledgeReviewApi';
import { SAMPLE_GAPS } from './sampleData';

/*
 * Reminder gaps (AI Suite tab): questions guests ask after receiving the operator's reminders,
 * and the reminder changes the operator can request from TourOptima ops. Backend contract:
 * cplanetbackend/apiv2/views/edit/reminder_gaps.py, plan
 * cplanetbackend/aidocs/plans/2026-10-07-reminder-gaps.md.
 *
 * Like the training inbox: always visible outside production, with SAMPLE DATA unless the
 * operator's `reminder_gaps_enabled` is set; in production only when that flag is true, or "staff"
 * for the staff accounts (alpha) -- the same staff list as the training inbox's staff-only mode.
 * Real data whenever the flag lets this user in. Callers add the AI page permission check.
 */
const RESOURCE = 'tour_operator/reminder_gaps';
const STAFF_ONLY = 'staff';

const bootstrapUserEmail = () => {
  try {
    const email = JSON.parse(window.localStorage.getItem('bootstrap') || '{}')?.user?.email;
    return typeof email === 'string' ? email.trim().toLowerCase() : null;
  } catch {
    return null;
  }
};

// Real data: the flag lets this user in.
export const reminderGapsLiveFor = (tourOperator) => {
  const mode = tourOperator?.config?.reminder_gaps_enabled;
  return mode === true || (mode === STAFF_ONLY && KNOWLEDGE_REVIEW_STAFF_EMAILS.includes(bootstrapUserEmail()));
};

export const reminderGapsVisibleFor = (tourOperator) => !config.isProduction || reminderGapsLiveFor(tourOperator);

// Reminder event-code suffixes -> what guests call them.
export const REMINDER_LABELS = {
  '2h': '2-hour reminder',
  '20h': '20-hour reminder',
  '48h': '48-hour reminder',
  '72h': '72-hour reminder',
  '5d': '5-day message',
  '7d': '7-day message',
  postbooking3h: 'post-booking message',
  postbooking1h: 'post-booking message',
  post48h: 'post-tour message',
  endtrip1h: 'end-of-tour message',
};

// Any other reminder code ("ontrip4h", "36h") in words rather than as a code.
export const reminderLabel = (code) => {
  if (REMINDER_LABELS[code]) return REMINDER_LABELS[code];
  const m = /^(\d+)([hd])$/.exec(code || '');
  if (m) return `${m[1]}-${m[2] === 'h' ? 'hour' : 'day'} reminder`;
  return (code || '').startsWith('ontrip') ? 'on-tour message' : (code || 'reminder');
};

export const KIND_LABELS = {
  product: 'Product info',
  template: 'Reminder template',
  app: 'Timing or app issue',
};

// The suggestion template ("Tickets are {1}") with the operator's answers filled in.
export const fillTemplate = (suggestion, answers) => {
  const template = suggestion?.template || '';
  return template.replace(/\{(\d+)\}/g, (m, n) => {
    const i = Number(n);
    const value = answers?.[i] ?? suggestion?.blanks?.[i - 1]?.default ?? '';
    return value ? String(value) : `[${suggestion?.blanks?.[i - 1]?.label || 'detail'}]`;
  });
};

// Where the fix goes, in the operator's words.
export const describeTarget = (gap) => {
  const t = gap?.target || {};
  if (t.type === 'tag_attr') {
    const field = { meet1_name: 'meeting point', meet1_directions: 'meeting point directions', reminders_20_hr: '20-hour reminder note' }[t.attr] || t.attr;
    return `Fix goes in: Product Info › ${gap.tour?.name || t.tag_name} › ${field}`;
  }
  if (t.type === 'template') return 'Fix goes in: the reminder template used for all tours. Our operations team edits it.';
  return 'The reminder already says this, but guests ask before it arrives or can’t find it in the app. Our team will look at the timing of your messages and the app.';
};

const STATUS_GROUP = { open: 'open', requested: 'requested', in_progress: 'requested', live: 'live', ignored: 'ignored' };

// In-memory preview: same shapes as the API; `ops` is the preview's "View as TourOptima ops" switch.
export function createMockApi() {
  let gaps = SAMPLE_GAPS.map((g) => ({ ...g }));
  let ops = false;
  const now = () => new Date().toISOString();
  const update = (id, patch) => {
    gaps = gaps.map((g) => (g.id === id ? { ...g, ...patch, status_group: STATUS_GROUP[patch.status || g.status] } : g));
    return Promise.resolve(gaps.find((g) => g.id === id));
  };
  const thread = (id, who, text) => update(id, { thread: [...(gaps.find((g) => g.id === id)?.thread || []), { who, ts: now(), text }] });
  return {
    mode: 'mock',
    setOps: (value) => { ops = value; },
    summary: () => Promise.resolve({
      ops,
      counts: ['open', 'requested', 'live', 'ignored'].reduce((acc, k) => ({ ...acc, [k]: gaps.filter((g) => g.status_group === k).length }), {}),
    }),
    list: (status) => Promise.resolve(gaps.filter((g) => g.status_group === status)
      .sort((a, b) => (b.evidence?.questions || 0) - (a.evidence?.questions || 0))),
    request: (id, data) => update(id, {
      status: 'requested', answers: data.answers || {}, requested_text: data.text || '', edited: !!data.edited,
      languages: data.languages || [], requested_ts: now(), requested_by: { id: 0, name: 'You' },
    }),
    ignore: (id) => update(id, { status: 'ignored' }),
    reopen: (id) => update(id, { status: 'open' }),
    reply: (id, text) => thread(id, ops ? 'ops' : 'operator', text),
    assign: (id) => update(id, { status: 'in_progress' }),
    markLive: (id) => {
      const g = gaps.find((x) => x.id === id);
      const langs = g?.languages?.includes('all') ? ['en', 'fr', 'es'] : (g?.languages || []);
      return update(id, { status: 'live', live_marked_ts: now(), verify: Object.fromEntries(langs.map((l) => [l, { seen: 0, sent: 0 }])) });
    },
    ask: (id, text) => thread(id, 'ops', text),
  };
}

export function reminderGapsApi(dataAPI, topId) {
  const scope = topId ? { tour_operator: topId } : {};
  const act = (id, action, data = {}) =>
    dataAPI(UPDATE, RESOURCE, { id, urlSuffix: `${action}/`, data: { ...data, ...scope } }).then((r) => r.data);
  return {
    mode: 'live',
    summary: () => dataAPI(GET_ONE, RESOURCE, { urlSuffix: 'summary/', filter: { ...scope } }).then((r) => r.data),
    list: (status) =>
      dataAPI(GET_LIST, RESOURCE, {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'id', order: 'DESC' },
        filter: { status, ...scope },
      }).then((r) => r.data),
    request: (id, data) => act(id, 'request', data),
    ignore: (id) => act(id, 'ignore'),
    reopen: (id) => act(id, 'reopen'),
    reply: (id, text) => act(id, 'reply', { text }),
    assign: (id) => act(id, 'assign'),
    markLive: (id) => act(id, 'mark_live'),
    ask: (id, text) => act(id, 'ask', { text }),
  };
}
